import React, {useCallback, useEffect, useState} from 'react'
import {MunshiMenu} from "../components/Menu";
import {Card} from "primereact/card";
import {Button} from 'primereact/button'
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {ToggleButton} from 'primereact/togglebutton';
import {FloatLabel} from "primereact/floatlabel";
import {ToastContainer} from 'react-toastify';
import {DepartmentReselect} from "../components/Select/DepartmentReselect";
import {GroupByDepartmentSelect} from "../components/Select/GroupByDepartmentSelect";
import {CustomerByGroupSelect} from "../components/Select/CustomerByGroupSelect";
import {CustomerGroupCategorySelect} from "../components/Select/CustomerGroupCatagorySelect";
import {axiosInstance} from "../utilities/AxiosConfig";
import {useSpinner} from "../context/SpinnerContext";
import {notify} from "../components/toast";
import {Auths} from "../utilities/getAuthDep";

export function CustomerEditor({handle}) {
    const {loading, setLoading} = useSpinner();
    const [level] = useState(Auths);
    const [tempCustomerName, setTempCustomerName] = useState("");

    // Grouped selections
    const [selections, setSelections] = useState({
        department: {id: 0, name: ""},
        group: {
            id: 0,
            name: "",
            status: "T",
            sales: true,
            department: 0,
            removable: true,
            sms: ""
        },
        customer: {
            id: 0,
            code: "0000",
            name: "",
            customerGroup: 0,
            initialBalance: 0,
            showGroup: false,
            sms: "",
            closingBalance: 0
        }
    });

    const [lists, setLists] = useState({
        departments: [],
        groups: [],
        customers: []
    });

    const [visibility, setVisibility] = useState({
        showGroup: false,
        showCustomer: false
    });

    const fetchData = useCallback(async (url, targetKey) => {
        setLoading(true);
        try {
            const {data} = await axiosInstance.get(url);
            setLists(prev => ({...prev, [targetKey]: data || []}));
        } catch (error) {
            notify(error.message, "error");
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    useEffect(() => {
        fetchData("department/", "departments");
    }, [fetchData]);

    useEffect(() => {
        if (selections.department.id) {
            fetchData(`customerGroup/department/${selections.department.id}`, "groups");
        }
    }, [selections.department.id, fetchData]);

    useEffect(() => {
        if (selections.group.id) {
            fetchData(`customer/group/${selections.group.id}`, "customers");
        }
    }, [selections.group.id, fetchData]);

    useEffect(() => {
        setTempCustomerName(selections.customer.name);
    }, [selections.customer.name]);

    const handleDepartmentSelect = (dept) => {
        setSelections(prev => ({
            ...prev,
            department: dept,
            group: {id: 0, name: "", status: "T", sales: true, department: dept.id, removable: true, sms: ""},
            customer: {id: 0, code: "0000", name: "", customerGroup: 0, initialBalance: 0, showGroup: false, sms: ""}
        }));
        setVisibility({showGroup: dept.id !== 0, showCustomer: false});
    };

    const handleGroupSelect = (group) => {
        setSelections(prev => ({
            ...prev,
            group: group,
            customer: {id: 0, code: "0000", name: "", customerGroup: group.id, initialBalance: 0, showGroup: false, sms: ""}
        }));
        setVisibility(prev => ({...prev, showCustomer: group.id !==0}));
    };

    const performAction = async (action, target, data) => {
        setLoading(true);
        try {
            if (action === "Save") {
                await axiosInstance.post(`${target}/`, data);
                notify(`${target} Saved Successfully`, "success");
            } else if (action === "Delete") {
                const res = await axiosInstance.delete(`${target}/${data.id}`);
                if (res.data === 0) throw new Error(`${target} could not be deleted`);
                notify(`${target} Deleted Successfully`, "success");
            }

            if (target === "department") {
                fetchData("department/", "departments");
                handleDepartmentSelect({id: 0, name: ""});
                setVisibility({showGroup: false, showCustomer: false});
            } else if (target === "customerGroup") {
                fetchData(`customerGroup/department/${selections.department.id}`, "groups");
                handleGroupSelect({id: 0, name: "", department: selections.department.id});
                setVisibility(prev => ({...prev, showCustomer: false}));
            } else if (target === "customer") {
                fetchData(`customer/group/${selections.group.id}`, "customers");
                setSelections(prev => ({...prev, customer: {id: 0, code: "0000", name: "", customerGroup: selections.group.id}}));
            }
        } catch (error) {
            notify(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const EditorActions = ({onSave, onDelete, onReset, isNew}) => (
        <div className="flex gap-2 mt-2">
            <Button icon="pi pi-undo" className="p-button-secondary p-button-sm" onClick={onReset} tooltip="Reset" />
            <Button icon="pi pi-save" className="p-button-success p-button-sm" onClick={onSave} tooltip="Save" />
            {!isNew && <Button icon="pi pi-trash" className="p-button-danger p-button-sm" onClick={onDelete} tooltip="Delete" />}
        </div>
    );
    if(!loading) {
        // Moved MainBody outside or transformed it into a regular part of the render to avoid remounting
        return (
            <div className="min-h-screen bg-gray-50 pb-6">
                <ToastContainer autoClose={1500} position="top-right" hideProgressBar/>
                <MunshiMenu handle={handle}/>
                <div className="container mx-auto px-3 mt-4">
                    <div className="grid justify-content-center">
                        <div className="col-12">
                            <Card title={
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-users text-primary text-2xl"></i>
                                    <span>Customer & Group Editor</span>
                                </div>
                            } className="shadow-2 border-round-xl">
                                <div className="grid py-2">
                                    {/* Department Section - 1/3 width on md and lg */}
                                    <div className="col-12 md:col-4 lg:col-4">
                                        <section className="p-3 border-round surface-border border-1 surface-50 h-full">
                                            <label className="block mb-3 font-bold text-sm text-600">DEPARTMENT</label>
                                            <div className="flex flex-column gap-4">
                                                <DepartmentReselect
                                                    department={selections.department}
                                                    departmentList={lists.departments}
                                                    setDepartment={handleDepartmentSelect}
                                                    disabled={false}/>
                                                <FloatLabel>
                                                    <InputText
                                                        className="w-full h-3rem"
                                                        maxLength={24}
                                                        value={selections.department.name}
                                                        onChange={(e) => setSelections(prev => ({
                                                            ...prev,
                                                            department: {...prev.department, name: e.target.value}
                                                        }))}
                                                    />
                                                    <label>Edit Department Name</label>
                                                </FloatLabel>
                                                <EditorActions
                                                    isNew={!selections.department.id}
                                                    onReset={() => handleDepartmentSelect({id: 0, name: ""})}
                                                    onSave={() => performAction("Save", "department", selections.department)}
                                                    onDelete={() => performAction("Delete", "department", selections.department)}
                                                />
                                            </div>
                                        </section>
                                    </div>

                                    {/* Group Section - 1/3 width on md and lg */}
                                    <div className="col-12 md:col-4 lg:col-4">
                                        {visibility.showGroup ? (
                                            <section
                                                className="p-3 border-round surface-border border-1 surface-50 animation-duration-300 fadein h-full">
                                                <label className="block mb-3 font-bold text-sm text-600">CUSTOMER GROUP</label>
                                                <div className="flex flex-column gap-4">
                                                    <GroupByDepartmentSelect
                                                        group={selections.group}
                                                        groupList={lists.groups}
                                                        setGroup={handleGroupSelect}
                                                        disable={false}/>
                                                    <div className="flex flex-column gap-1">
                                                        <label className="text-xs font-semibold text-500">Change Group Department</label>
                                                        <DepartmentReselect
                                                            department={{id: selections.group.department}}
                                                            departmentList={lists.departments}
                                                            setDepartment={(dept) => setSelections(prev => ({
                                                                ...prev,
                                                                group: {...prev.group, department: dept.id}
                                                            }))}
                                                            disabled={false}/>
                                                    </div>
                                                    <div className="grid">
                                                        <div className="col-12 xl:col-6">
                                                            <FloatLabel>
                                                                <InputText className="w-full h-3rem"
                                                                           value={selections.group.name}
                                                                           onChange={(e) => setSelections(prev => ({
                                                                               ...prev,
                                                                               group: {...prev.group, name: e.target.value}
                                                                           }))}/>
                                                                <label>Group Name</label>
                                                            </FloatLabel>
                                                        </div>
                                                        <div className="col-12 xl:col-6">
                                                            <CustomerGroupCategorySelect
                                                                groupCat={{value: selections.group.status}}
                                                                setGroupCat={(cat) => setSelections(prev => ({
                                                                    ...prev,
                                                                    group: {...prev.group, status: cat.value}
                                                                }))}
                                                                disable={false}/>
                                                        </div>
                                                    </div>
                                                    <FloatLabel>
                                                        <InputText className="w-full h-3rem"
                                                                   maxLength={24}
                                                                   value={selections.group.sms || ""}
                                                                   onChange={(e) => setSelections(prev => ({
                                                                       ...prev,
                                                                       group: {...prev.group, sms: e.target.value}
                                                                   }))}/>
                                                        <label>Group SMS Template</label>
                                                    </FloatLabel>
                                                    <div className="flex flex-column xl:flex-row gap-2">
                                                        <ToggleButton onLabel="Sale Group" offLabel="No Sale"
                                                                      checked={selections.group.sales}
                                                                      onChange={(e) => setSelections(prev => ({
                                                                          ...prev,
                                                                          group: {...prev.group, sales: e.value}
                                                                      }))} className="w-full h-3rem"/>
                                                        <ToggleButton onLabel="Removable" offLabel="Permanent"
                                                                      checked={selections.group.removable}
                                                                      onChange={(e) => setSelections(prev => ({
                                                                          ...prev,
                                                                          group: {...prev.group, removable: e.value}
                                                                      }))} className="w-full h-3rem"/>
                                                    </div>
                                                    <EditorActions
                                                        isNew={!selections.group.id}
                                                        onReset={() => handleGroupSelect({
                                                            id: 0,
                                                            name: "",
                                                            department: selections.department.id
                                                        })}
                                                        onSave={() => performAction("Save", "customerGroup", selections.group)}
                                                        onDelete={() => performAction("Delete", "customerGroup", selections.group)}
                                                    />
                                                </div>
                                            </section>
                                        ) : <div className="p-3 border-round border-dashed border-1 surface-border surface-50 h-full flex align-items-center justify-content-center text-400">Select Department first</div>}
                                    </div>

                                    {/* Customer Section - 1/3 width on md and lg */}
                                    <div className="col-12 md:col-4 lg:col-4">
                                        {visibility.showCustomer ? (
                                            <section
                                                className="p-3 border-round surface-border border-1 surface-50 animation-duration-300 fadein h-full">
                                                <label className="block mb-3 font-bold text-sm text-600">CUSTOMER DETAILS</label>
                                                <div className="flex flex-column gap-4">
                                                    <CustomerByGroupSelect
                                                        customer={selections.customer}
                                                        customerList={lists.customers}
                                                        setCustomer={(c) => setSelections(prev => ({...prev, customer: c}))}
                                                        disable={false}/>
                                                    <FloatLabel>
                                                        <InputText
                                                            className="w-full h-3rem"
                                                            maxLength={24}
                                                            value={tempCustomerName}
                                                            onChange={(e) => setTempCustomerName(e.target.value)}
                                                            onBlur={() => setSelections(prev => ({
                                                                ...prev,
                                                                customer: {...prev.customer, name: tempCustomerName}
                                                            }))}
                                                        />
                                                        <label>Customer Name</label>
                                                    </FloatLabel>
                                                    <div className="grid">
                                                        <div className="col-12 xl:col-6">
                                                            <FloatLabel>
                                                                <InputNumber className="w-full h-3rem"
                                                                             value={selections.customer.initialBalance}
                                                                             onValueChange={(e) => setSelections(prev => ({
                                                                                 ...prev,
                                                                                 customer: {
                                                                                     ...prev.customer,
                                                                                     initialBalance: e.value
                                                                                 }
                                                                             }))} mode="currency" currency="PKR"
                                                                             locale="en-IN"/>
                                                                <label>Initial Balance</label>
                                                            </FloatLabel>
                                                        </div>
                                                        <div className="col-12 xl:col-6">
                                                            <FloatLabel>
                                                                <InputText className="w-full h-3rem"
                                                                           value={selections.customer.sms || ""}
                                                                           onChange={(e) => setSelections(prev => ({
                                                                               ...prev,
                                                                               customer: {
                                                                                   ...prev.customer,
                                                                                   sms: e.target.value
                                                                               }
                                                                           }))}/>
                                                                <label>Phone / SMS</label>
                                                            </FloatLabel>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-column gap-1">
                                                        <label className="text-xs font-semibold text-500">Change Customer Group</label>
                                                        <GroupByDepartmentSelect
                                                            group={{id: selections.customer.customerGroup}}
                                                            groupList={lists.groups}
                                                            setGroup={(g) => setSelections(prev => ({
                                                                ...prev,
                                                                customer: {...prev.customer, customerGroup: g.id}
                                                            }))}
                                                            disable={false}
                                                        />
                                                    </div>
                                                    <ToggleButton onLabel="Show Group In Print"
                                                                  offLabel="Hide Group In Print"
                                                                  checked={selections.customer.showGroup}
                                                                  onChange={(e) => setSelections(prev => ({
                                                                      ...prev,
                                                                      customer: {...prev.customer, showGroup: e.value}
                                                                  }))} className="w-full h-3rem"/>
                                                    <EditorActions
                                                        isNew={!selections.customer.id}
                                                        onReset={() => setSelections(prev => ({
                                                            ...prev,
                                                            customer: {
                                                                id: 0,
                                                                code: "0000",
                                                                name: "",
                                                                customerGroup: selections.group.id
                                                            }
                                                        }))}
                                                        onSave={() => performAction("Save", "customer", selections.customer)}
                                                        onDelete={() => performAction("Delete", "customer", selections.customer)}
                                                    />
                                                </div>
                                            </section>
                                        ) : <div className="p-3 border-round border-dashed border-1 surface-border surface-50 h-full flex align-items-center justify-content-center text-400">Select Group first</div>}
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

export default CustomerEditor;
